import React, { useEffect, useMemo, useState } from "react";
import PageLayout from "../../components/layout/PageLayout";
import DashboardDrilldownBanner from "../../components/dashboard/DashboardDrilldownBanner";
import {
  useDashboardDrilldown,
  useDrilldownFilterSync,
} from "../../hooks/useDashboardDrilldown";
import { LIST_STATUS_PARAM } from "../../components/dashboard/dashboardDrilldown";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { extractPagination } from "../../utils/response";
import { toast } from "react-toastify";
import Can from "../../components/permissions/Can";
import VisibilityIcon from "@mui/icons-material/Visibility";
// import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ORDER_CREATE_PATH,
  ORDER_VIEW_PATH,
  INVOICES_CREATE_PATH,
} from "../../navigations/path";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useGetOrdersQuery } from "../../store/api";

const ORDER_STATUS_OPTIONS = [
  "unpaid",
  "partial",
  "cancelled",
  "paid",
  "refunded",
  "chargeback",
  "voided",
];

const statusLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const OrderList = () => {
  const navigate = useNavigate();
  const {
    active: drilldownActive,
    params: drilldownParams,
    clear: clearDrilldown,
  } = useDashboardDrilldown();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  // Cancelled / refunded / chargeback / voided cards open this list with that status selected.
  const [statusFilter, setStatusFilter] = useState(() => {
    const fromCard = searchParams.get(LIST_STATUS_PARAM);
    return ORDER_STATUS_OPTIONS.includes(fromCard) ? fromCard : "";
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Paid / partial / unpaid cards select the matching order status in the filter.
  const detachDrilldown = useDrilldownFilterSync({
    active: drilldownActive,
    value: ORDER_STATUS_OPTIONS.includes(drilldownParams.dd_metric)
      ? drilldownParams.dd_metric
      : undefined,
    apply: setStatusFilter,
    reset: () => setStatusFilter(""),
    clear: clearDrilldown,
  });

  const queryParams = useMemo(
    () => ({
      page,
      per_page: perPage,
      paginate: 1,
      "relations[0]": "lead",
      "relations[1]": "brand",
      ...(search ? { search } : {}),
      ...(statusFilter ? { "filters[status]": statusFilter } : {}),
      ...drilldownParams,
    }),
    [page, perPage, search, statusFilter, drilldownParams],
  );

  const { data, isLoading, isFetching, error } = useGetOrdersQuery(queryParams);

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || "Failed to load orders");
    }
  }, [error]);

  const rows = useMemo(() => {
    const list =
      data?.data?.list ??
      data?.list ??
      (Array.isArray(data?.data) ? data.data : []);
    return Array.isArray(list) ? list : [];
  }, [data]);

  const total = extractPagination(data)?.total ?? 0;
  const loading = isLoading || isFetching;

  const headCellSx = { fontWeight: 600, whiteSpace: "nowrap" };

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPage(1);
    setPerPage(per);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage + 1);
  };

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleClearFilters = () => {
    detachDrilldown();
    setSearchInput("");
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  // const handleCopyOrderId = async () => {
  //   const id = selectedRow?.id;
  //   handleCloseMenu();
  //   if (!id) return;
  //   try {
  //     await navigator.clipboard.writeText(String(id));
  //     toast.success("Order ID copied to clipboard");
  //   } catch {
  //     toast.error("Could not copy order ID");
  //   }
  // };

  const handleCreateInvoice = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (!id) return;
    navigate(`${INVOICES_CREATE_PATH}?orderId=${encodeURIComponent(id)}`);
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <DashboardDrilldownBanner />
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: "primary.main", fontWeight: 600 }}
          >
            Order List
          </Typography>
          <Can permission="order-create">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(ORDER_CREATE_PATH)}
            >
              Create Order
            </Button>
          </Can>
        </Box>

        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search orders..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                detachDrilldown();
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {ORDER_STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {statusLabel(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="text" onClick={handleClearFilters}>
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ overflowX: "auto" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    <TableCell sx={headCellSx}>Order No.</TableCell>
                    <TableCell sx={headCellSx}>Lead Name</TableCell>
                    <TableCell sx={headCellSx}>Lead Email</TableCell>
                    <TableCell sx={headCellSx}>Brand</TableCell>
                    {/* <TableCell sx={headCellSx}>Account descriptor</TableCell> */}
                    <TableCell sx={headCellSx}>Sale Type</TableCell>
                    <TableCell sx={headCellSx}>Amount</TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                    <TableCell sx={headCellSx}>City</TableCell>
                    <TableCell sx={headCellSx}>Created At</TableCell>
                    <TableCell
                      sx={{ ...headCellSx, textAlign: "center", width: 70 }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No orders found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, index) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          {/* {(page - 1) * perPage + index + 1} */}
                          {row.order_number ?? "_"}
                        </TableCell>

                        <TableCell>{row.lead?.name ?? "—"}</TableCell>
                        <TableCell>{row.lead?.email ?? "—"}</TableCell>

                        <TableCell>{row.brand?.name ?? "—"}</TableCell>
                        {/* <TableCell>
                                                    {row.account?.descriptor ??
                                                        row.account?.name ??
                                                        "—"}
                                                </TableCell> */}

                        <TableCell>
                          {row.sale_type
                            ?.replace(/[-_]/g, " ")
                            ?.replace(/\b\w/g, (c) => c.toUpperCase())}
                        </TableCell>

                        <TableCell>${row.amount}</TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={row.status}
                            color={
                              row.status === "paid"
                                ? "success"
                                : row.status === "unpaid"
                                  ? "warning"
                                  : "default"
                            }
                          />
                        </TableCell>

                        <TableCell>{row.city}</TableCell>

                        <TableCell>{row.created_at}</TableCell>

                        <TableCell
                          sx={{ textAlign: "center" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={total}
                page={page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={perPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Can permission="order-view">
          <MenuItem
            onClick={() =>
              navigate(ORDER_VIEW_PATH.replace(":id", selectedRow?.id))
            }
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="invoice-create">
          <MenuItem onClick={handleCreateInvoice}>
            <ListItemIcon>
              <ReceiptLongIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create invoice</ListItemText>
          </MenuItem>
        </Can>
        {/* <MenuItem onClick={handleCopyOrderId}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy order ID</ListItemText>
        </MenuItem> */}
      </Menu>
    </PageLayout>
  );
};

export default OrderList;
